"use client";

import { c as _c } from "react/compiler-runtime";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, startTransition } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { isNetworkError, notifyWakeSignals, registerWakeRecoveryJob, setWakeRecoveryDebounceMs, setWakeRecoveryLogger, unregisterWakeRecoveryJob } from "@sukha/query";
import { tryRunForegroundRecovery } from "@/lib/client/foregroundRecoveryCoordinator";
import Logger from "@/lib/utils/logger";
type NetworkStatus = "online" | "offline" | "reconnecting";
interface NetworkStatusContextType {
  status: NetworkStatus;
  isOnline: boolean;
  isReconnecting: boolean;
  lastOnlineTime: number | null;
  timeSinceOffline: number;
}
const NetworkStatusContext = createContext<NetworkStatusContextType>({
  status: "online",
  isOnline: true,
  isReconnecting: false,
  lastOnlineTime: null,
  timeSinceOffline: 0
});
export const useNetworkStatus = () => {
  return useContext(NetworkStatusContext);
};
interface NetworkStatusProviderProps {
  children: React.ReactNode;
}
const WAKE_JOB_KEY = "network-status";
export function NetworkStatusProvider(t0) {
  const $ = _c(36);
  const {
    children
  } = t0;
  const [status, setStatus] = useState("online");
  const [lastOnlineTime, setLastOnlineTime] = useState(null);
  const [timeSinceOffline, setTimeSinceOffline] = useState(0);
  const queryClient = useQueryClient();
  const reconnectTimeoutRef = useRef(null);
  const statusRef = useRef(status);
  let t1;
  let t2;
  if ($[0] !== status) {
    t1 = () => {
      statusRef.current = status;
    };
    t2 = [status];
    $[0] = status;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== queryClient) {
    t3 = () => {
      queryClient.invalidateQueries({
        predicate: _temp
      });
    };
    $[3] = queryClient;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const invalidateFailedNetworkQueries = t3;
  let t4;
  if ($[5] !== queryClient) {
    t4 = () => {
      Logger.debug("[NetworkStatus] Network went offline");
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      setStatus("offline");
      setLastOnlineTime(Date.now());
      queryClient.getQueryCache().getAll().forEach(query_0 => {
        queryClient.cancelQueries({
          queryKey: query_0.queryKey
        });
      });
    };
    $[5] = queryClient;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const handleOffline = t4;
  let t5;
  if ($[7] !== invalidateFailedNetworkQueries || $[8] !== queryClient) {
    t5 = () => {
      Logger.debug("[NetworkStatus] Network came back online");
      setStatus("reconnecting");
      setLastOnlineTime(null);
      setTimeSinceOffline(0);
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      reconnectTimeoutRef.current = setTimeout(() => {
        reconnectTimeoutRef.current = null;
        setStatus("online");
        queryClient.invalidateQueries({
          queryKey: ["activeSession"]
        });
        invalidateFailedNetworkQueries();
        Logger.debug("[NetworkStatus] Reconnection complete");
      }, 1500);
    };
    $[7] = invalidateFailedNetworkQueries;
    $[8] = queryClient;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const beginOnlineRecovery = t5;
  let t6;
  if ($[10] !== beginOnlineRecovery || $[11] !== invalidateFailedNetworkQueries) {
    t6 = () => {
      if (typeof window === "undefined" || !navigator.onLine) {
        return;
      }
      const s = statusRef.current;
      if (s !== "online") {
        beginOnlineRecovery();
        return;
      }
      tryRunForegroundRecovery("network-status", () => {
        invalidateFailedNetworkQueries();
      });
    };
    $[10] = beginOnlineRecovery;
    $[11] = invalidateFailedNetworkQueries;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const runWakeNetworkJob = t6;
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = [];
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  useEffect(_temp2, t7);
  let t8;
  let t9;
  if ($[14] !== runWakeNetworkJob) {
    t8 = () => {
      setWakeRecoveryLogger({
        debug: _temp3
      });
      registerWakeRecoveryJob(WAKE_JOB_KEY, runWakeNetworkJob);
      return _temp4;
    };
    t9 = [runWakeNetworkJob];
    $[14] = runWakeNetworkJob;
    $[15] = t8;
    $[16] = t9;
  } else {
    t8 = $[15];
    t9 = $[16];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[17] !== handleOffline) {
    t10 = () => {
      if (typeof window === "undefined") {
        return;
      }
      if (!navigator.onLine) {
        startTransition(() => {
          handleOffline();
        });
      }
      const onOnline = _temp5;
      window.addEventListener("online", onOnline);
      window.addEventListener("offline", handleOffline);
      return () => {
        window.removeEventListener("online", onOnline);
        window.removeEventListener("offline", handleOffline);
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
      };
    };
    t11 = [handleOffline];
    $[17] = handleOffline;
    $[18] = t10;
    $[19] = t11;
  } else {
    t10 = $[18];
    t11 = $[19];
  }
  useEffect(t10, t11);
  let t12;
  let t13;
  if ($[20] !== handleOffline) {
    t12 = () => {
      if (typeof document === "undefined") {
        return;
      }
      const handleVisibilityChange = () => {
        if (document.visibilityState === "visible") {
          if (navigator.onLine && statusRef.current !== "online") {
            Logger.debug("[NetworkStatus] App resumed, verifying network connection");
            startTransition(_temp6);
          } else {
            if (!navigator.onLine && statusRef.current !== "offline") {
              startTransition(() => {
                handleOffline();
              });
            } else {
              if (navigator.onLine) {
                notifyWakeSignals("visibility_visible");
              }
            }
          }
        }
      };
      document.addEventListener("visibilitychange", handleVisibilityChange);
      return () => {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
      };
    };
    t13 = [handleOffline];
    $[20] = handleOffline;
    $[21] = t12;
    $[22] = t13;
  } else {
    t12 = $[21];
    t13 = $[22];
  }
  useEffect(t12, t13);
  let t14;
  let t15;
  if ($[23] !== lastOnlineTime || $[24] !== status) {
    t14 = () => {
      if (status !== "offline") {
        return;
      }
      const interval = setInterval(() => {
        if (lastOnlineTime) {
          startTransition(() => {
            setTimeSinceOffline(Date.now() - lastOnlineTime);
          });
        }
      }, 1000);
      return () => clearInterval(interval);
    };
    t15 = [status, lastOnlineTime];
    $[23] = lastOnlineTime;
    $[24] = status;
    $[25] = t14;
    $[26] = t15;
  } else {
    t14 = $[25];
    t15 = $[26];
  }
  useEffect(t14, t15);
  const t16 = status === "online";
  const t17 = status === "reconnecting";
  let t18;
  if ($[27] !== lastOnlineTime || $[28] !== status || $[29] !== t16 || $[30] !== t17 || $[31] !== timeSinceOffline) {
    t18 = {
      status,
      isOnline: t16,
      isReconnecting: t17,
      lastOnlineTime,
      timeSinceOffline
    };
    $[27] = lastOnlineTime;
    $[28] = status;
    $[29] = t16;
    $[30] = t17;
    $[31] = timeSinceOffline;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  const value = t18;
  let t19;
  if ($[33] !== children || $[34] !== value) {
    t19 = <NetworkStatusContext.Provider value={value}>{children}</NetworkStatusContext.Provider>;
    $[33] = children;
    $[34] = value;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  return t19;
}
function _temp6() {
  notifyWakeSignals("visibility_visible");
}
function _temp5() {
  notifyWakeSignals("online");
}
function _temp4() {
  unregisterWakeRecoveryJob(WAKE_JOB_KEY);
  setWakeRecoveryLogger(null);
}
function _temp3(data, message) {
  Logger.debug(message, data);
}
function _temp2() {
  if (typeof window !== "undefined" && window.matchMedia("(display-mode: standalone)").matches) {
    setWakeRecoveryDebounceMs(800);
  }
}
function _temp(query) {
  return query.state.status === "error" && query.state.error != null && isNetworkError(query.state.error as Error);
}